import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Link } from "react-router-dom";

const TIMELINE = [
  { year: "2019", label: "Kitchen", body: "First bottle poured for family, on a Sunday, from a scratched steel jug." },
  { year: "2021", label: "Booth", body: "A friend lent us a folding table at a farmers market. The bottles were gone by noon." },
  { year: "2023", label: "Bottling", body: "A small production house of our own — recyclable glass, thoughtful labels, slow lines." },
  { year: "2025", label: "Beyond", body: "Now poured in a handful of upscale cafés and stocked on shelves we admire." },
];

const HERO_IMG = "/about-hero-market.png";

// Premium 3D tilt card — mouse-tracked rotation + scroll parallax
function TiltCard({ src, alt }) {
  const ref = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springCfg = { stiffness: 140, damping: 18, mass: 0.6 };
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [10, -10]), springCfg);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-14, 14]), springCfg);
  const translateZ = useSpring(0, springCfg);

  // Scroll-driven subtle vertical parallax on the inner image
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1.12, 1.08]);

  const glareX = useTransform(mouseX, [-0.5, 0.5], ["20%", "80%"]);
  const glareY = useTransform(mouseY, [-0.5, 0.5], ["20%", "80%"]);

  function handleMove(e) {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(px);
    mouseY.set(py);
  }
  function handleEnter() {
    translateZ.set(30);
  }
  function handleLeave() {
    mouseX.set(0);
    mouseY.set(0);
    translateZ.set(0);
  }

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="relative"
      style={{ perspective: 1400 }}
    >
      {/* Soft floor shadow that follows the tilt */}
      <motion.div
        aria-hidden
        className="absolute -inset-x-6 -bottom-8 h-24 rounded-[50%] blur-3xl bg-[color:var(--sr-primary)]/25"
        style={{
          x: useTransform(mouseX, [-0.5, 0.5], [24, -24]),
          scaleX: useTransform(mouseY, [-0.5, 0.5], [1.05, 0.85]),
          opacity: useTransform(mouseY, [-0.5, 0.5], [0.35, 0.65]),
        }}
      />

      <motion.div
        style={{
          rotateX,
          rotateY,
          translateZ,
          transformStyle: "preserve-3d",
        }}
        className="relative aspect-[4/3] w-full overflow-hidden bg-[color:var(--sr-secondary)] will-change-transform shadow-[0_40px_80px_-30px_rgba(20,45,25,0.35)]"
      >
        <motion.img
          src={src}
          alt={alt}
          style={{ y: imgY, scale: imgScale, transformStyle: "preserve-3d" }}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Depth layer: subtle gradient veil in front, receding into 3D */}
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            translateZ: 30,
            background:
              "linear-gradient(140deg, rgba(255,251,244,0) 55%, rgba(20,45,25,0.18) 100%)",
          }}
        />

        {/* Cursor-tracked light glare */}
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none mix-blend-soft-light"
          style={{
            translateZ: 40,
            background: useTransform(
              [glareX, glareY],
              ([x, y]) =>
                `radial-gradient(400px circle at ${x} ${y}, rgba(255,255,255,0.55), rgba(255,255,255,0) 60%)`
            ),
          }}
        />

        {/* Inner beveled edge */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/20"
          style={{ transform: "translateZ(50px)" }}
        />
      </motion.div>

      {/* Floating caption chip that lifts in 3D */}
      <motion.div
        style={{ rotateX, rotateY, translateZ: 60, transformStyle: "preserve-3d" }}
        className="absolute left-6 bottom-6 md:left-8 md:bottom-8"
      >
        <div className="backdrop-blur-md bg-[color:var(--sr-bg)]/75 border border-[color:var(--sr-primary)]/15 px-4 py-2 text-[10px] tracking-[0.28em] uppercase text-[color:var(--sr-primary)]">
          Orlando · Est. 2019
        </div>
      </motion.div>
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[color:var(--sr-bg)] pt-28 md:pt-32">
      {/* Hero */}
      <section className="sr-container">
        <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
          <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Our story
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="font-serif text-6xl md:text-8xl lg:text-[9rem] leading-[0.9] tracking-tight text-[color:var(--sr-primary)] max-w-5xl"
        >
          Made in small hours,
          <br />
          <em className="italic font-light text-[color:var(--sr-accent)]">
            with slow hands.
          </em>
        </motion.h1>

        <div className="grid grid-cols-12 gap-6 md:gap-10 mt-16 md:mt-24 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1 }}
            className="col-span-12 lg:col-span-7"
          >
            <TiltCard src={HERO_IMG} alt="The Smiling Roots at the farmers market" />
          </motion.div>
          <div className="col-span-12 lg:col-span-4 lg:col-start-9 space-y-6 text-[color:var(--sr-text)]/75 leading-relaxed">
            <p>
              The Smiling Roots began as a quiet obsession — the belief that
              the beverages we reach for every day could be made with the
              same care we reserve for the meals we cook slowly.
            </p>
            <p>
              We are a small company. We source close to home. We taste
              obsessively. Every bottle is composed by hand, in short runs,
              with attention paid where attention matters.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="sr-container py-24 md:py-40">
        <div className="grid grid-cols-12 gap-6">
          <p className="col-span-12 md:col-span-3 label-eyebrow text-[color:var(--sr-primary)] flex items-center gap-3">
            <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Mission
          </p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-9 font-serif text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-tight text-[color:var(--sr-primary)]"
          >
            To bottle something quietly beautiful — a small ritual for the
            everyday, made without shortcuts and without noise.
          </motion.h2>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-[color:var(--sr-secondary)] grain py-24 md:py-40" data-testid="about-timeline">
        <div className="sr-container">
          <div className="flex items-end justify-between mb-16">
            <p className="label-eyebrow text-[color:var(--sr-primary)] flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Timeline
            </p>
            <p className="hidden md:block label-eyebrow text-[color:var(--sr-muted)]">
              Small hands · long hours
            </p>
          </div>
          <ol className="relative border-l hairline pl-8 md:pl-14 space-y-16 md:space-y-24">
            {TIMELINE.map((t, i) => (
              <motion.li
                key={t.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.8, delay: i * 0.05 }}
                className="grid grid-cols-12 gap-4 items-start"
                data-testid={`timeline-${t.year}`}
              >
                <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-[color:var(--sr-accent)]" style={{ top: `calc(${i} * (12rem + 6rem))` }} />
                <p className="col-span-12 md:col-span-3 font-serif text-5xl md:text-6xl text-[color:var(--sr-primary)] leading-none">
                  {t.year}
                </p>
                <div className="col-span-12 md:col-span-9">
                  <p className="label-eyebrow text-[color:var(--sr-accent)] mb-3">{t.label}</p>
                  <p className="font-serif text-2xl md:text-4xl leading-tight text-[color:var(--sr-primary)] max-w-2xl">
                    {t.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <section className="sr-container py-24 md:py-32 text-center">
        <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6">A gentle invitation</p>
        <h3 className="font-serif text-4xl md:text-6xl text-[color:var(--sr-primary)] tracking-tight max-w-3xl mx-auto">
          Come find us at a market, or bring a bottle home.
        </h3>
        <div className="mt-10 flex flex-wrap justify-center gap-6">
          <Link
            to="/shop"
            className="px-8 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors"
            data-testid="about-cta-shop"
          >
            Shop the collection
          </Link>
          <Link
            to="/markets"
            className="px-8 py-4 border hairline text-[color:var(--sr-primary)] label-eyebrow hover:border-[color:var(--sr-primary)] transition-colors"
            data-testid="about-cta-markets"
          >
            Find a market
          </Link>
        </div>
      </section>
    </div>
  );
}
